import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, CircularProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { getTimelineMetrics, TimelineMetricsResponse } from '../services/channelDashboardService';
import { ApexOptions } from 'apexcharts';

interface TimelineChartProps {
    channelId: string;
    initialFilterType: string;
    initialStartDate?: string;
    initialEndDate?: string;
}

const ChannelDashboardTimelineChart: React.FC<TimelineChartProps> = ({ channelId, initialFilterType, initialStartDate, initialEndDate }) => {
    const muiTheme = useTheme();
    const isDark = muiTheme.palette.mode === 'dark';
    const [loading, setLoading] = useState(false);
    const [series, setSeries] = useState<ApexAxisChartSeries>([]);
    const [bucketSize, setBucketSize] = useState<string>('');
    const [viewRange, setViewRange] = useState<{ min: number, max: number } | null>(null);
    
    // We use a ref to track if a fetch is in progress to avoid race conditions or double fetching
    const fetchingRef = useRef(false);

    // Keep track of the current filter range
    // Initially 'all'
    const currentFilterRef = useRef({
        type: 'all',
        start: '',
        end: ''
    });

    const fetchData = useCallback(async (filterType: string, startDate?: string, endDate?: string) => {
        if (fetchingRef.current) return;
        
        try {
            fetchingRef.current = true;
            setLoading(true);

            let apiStartDate = startDate;
            let apiEndDate = endDate;

            // If custom range, calculate buffer for smooth panning
            if (filterType === 'custom' && startDate && endDate) {
                const start = new Date(startDate).getTime();
                const end = new Date(endDate).getTime();
                const diff = end - start;
                
                // Add 50% buffer on each side
                const bufferStart = start - (diff * 0.5);
                const bufferEnd = end + (diff * 0.5);

                apiStartDate = new Date(bufferStart).toISOString();
                apiEndDate = new Date(bufferEnd).toISOString();
                
                // Lock the visual range to the requested start/end
                setViewRange({ min: start, max: end });
            }
            // For non-custom we set viewRange only after we have the response (below), so don't clear it here

            const response: TimelineMetricsResponse = await getTimelineMetrics(channelId, filterType, apiStartDate, apiEndDate);
            
            // Transform data for ApexCharts
            const dataInbox = response.timeline_data.map(d => [new Date(d.timestamp).getTime(), d.inbox_count]);
            const dataSpam = response.timeline_data.map(d => [new Date(d.timestamp).getTime(), d.spam_count]);
            const dataNoReply = response.timeline_data.map(d => [new Date(d.timestamp).getTime(), d.no_reply_count]);

            setSeries([
                { name: 'Inbox', data: dataInbox },
                { name: 'Spam', data: dataSpam },
                { name: 'No-Reply', data: dataNoReply }
            ]);
            setBucketSize(response.bucket_size);

            // Center the chart on the data: set x-axis range to where values are non-zero (when not custom)
            if (filterType !== 'custom' && response.timeline_data.length > 0) {
                let dataMin: number | null = null;
                let dataMax: number | null = null;
                for (const d of response.timeline_data) {
                    const hasValue = (d.inbox_count ?? 0) > 0 || (d.spam_count ?? 0) > 0 || (d.no_reply_count ?? 0) > 0;
                    if (hasValue) {
                        const t = new Date(d.timestamp).getTime();
                        if (dataMin == null || t < dataMin) dataMin = t;
                        if (dataMax == null || t > dataMax) dataMax = t;
                    }
                }
                if (dataMin != null && dataMax != null) {
                    const span = dataMax - dataMin;
                    // Padding: at least 10% or 2 days so the range isn't too tight
                    const padding = Math.max(span * 0.15, 2 * 24 * 60 * 60 * 1000);
                    setViewRange({ min: dataMin - padding, max: dataMax + padding });
                } else {
                    setViewRange(null);
                }
            } else if (filterType === 'custom') {
                // viewRange already set above for custom
            } else {
                setViewRange(null);
            }

        } catch (error) {
            console.error('Failed to fetch timeline data', error);
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [channelId]);

    // Initial Load
    useEffect(() => {
        if (channelId) {
            fetchData(initialFilterType, initialStartDate, initialEndDate);
        }
    }, [channelId, initialFilterType, initialStartDate, initialEndDate]);

    // Debounce fetch to prevent API spam during zoom/pan
    const debouncedFetch = useMemo(() => {
        let timeout: NodeJS.Timeout;
        return (filterType: string, startDate: string, endDate: string) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                fetchData(filterType, startDate, endDate);
            }, 500);
        };
    }, [fetchData]);

    const handleChartUpdate = useCallback((chartContext: any, { xaxis }: { xaxis: { min: number, max: number } }) => {
        if (!xaxis) return;

        // Ensure we have a valid range
        if (xaxis.max - xaxis.min < 1000) return; // Ignore tiny ranges

        const startDate = new Date(xaxis.min).toISOString();
        const endDate = new Date(xaxis.max).toISOString();
        
        debouncedFetch('custom', startDate, endDate);
    }, [debouncedFetch]);

    const chartOptions: ApexOptions = useMemo(() => ({
        // Apex default tooltip/grid assume light mode; sync with MUI dark palette
        theme: {
            mode: isDark ? 'dark' : 'light',
        },
        chart: {
            type: 'line', // Line Chart
            stacked: false, 
            height: 350,
            foreColor: muiTheme.palette.text.secondary,
            zoom: {
                type: 'x',
                enabled: true,
                autoScaleYaxis: true
            },
            toolbar: {
                autoSelected: 'pan', 
                tools: {
                    download: false,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true, // Pan tool enabled
                    reset: false
                }
            },
            events: {
                zoomed: handleChartUpdate,
                scrolled: handleChartUpdate, 
                // When resetting, we fetch the global 'all' scope again
                beforeResetZoom: () => {
                     // Call directly to avoid delay
                     fetchData('all', '', ''); 
                }
            },
            animations: {
                enabled: false // fast updates
            }
        },
        colors: ['#1cabde', '#94a3b8', '#cbd5e1'], // Primary Blue, Gray, Light Gray
        dataLabels: { enabled: false },
        markers: { 
            size: 4, // Visible points
            hover: { size: 6 } 
        },
        stroke: { curve: 'smooth', width: 2 },
        tooltip: {
            shared: true,
            theme: isDark ? 'dark' : 'light',
            x: { format: 'dd MMM yyyy HH:mm' },
        },
        grid: {
            borderColor: muiTheme.palette.divider,
            strokeDashArray: 4
        },
        legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            labels: {
                colors: muiTheme.palette.text.secondary,
            },
        },
        xaxis: {
            type: 'datetime',
            tooltip: { enabled: false },
            min: viewRange?.min,
            max: viewRange?.max,
            labels: {
                datetimeUTC: false,
                style: { colors: muiTheme.palette.text.secondary },
            },
        },
        yaxis: {
            min: 0,
            forceNiceScale: true,
            labels: {
                style: { colors: muiTheme.palette.text.secondary },
            },
        },
    }), [handleChartUpdate, debouncedFetch, viewRange, fetchData, isDark, muiTheme]);

    return (
        <Box sx={{ width: '100%', height: '100%', minHeight: 400, position: 'relative' }}>
            {loading && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 0, left: 0, right: 0, bottom: 0, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    zIndex: 10,
                    bgcolor: alpha(muiTheme.palette.background.paper, 0.75),
                }}>
                    <CircularProgress size={30} sx={{ color: 'primary.main' }} />
                </Box>
            )}
            <Box id="chart" sx={{ height: '100%' }}>
                {/* Key forces remount when viewRange is set so ApexCharts applies xaxis min/max on initial render */}
                <ReactApexChart 
                    key={viewRange ? `range-${viewRange.min}-${viewRange.max}` : 'full'}
                    options={chartOptions} 
                    series={series} 
                    type="line" 
                    height={350} 
                    width="100%"
                />
            </Box>
        </Box>
    );
};

export default ChannelDashboardTimelineChart;
